import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { Check, X, ShieldCheck, Star, Target, Flame, ChevronDown, ChevronLeft, ChevronRight, Quote, Clock, BookOpen, Brain, Users, Trophy, Compass, FileText, Rocket, Layers, Infinity as InfinityIcon, ClipboardList, LifeBuoy, CheckSquare, Megaphone, CloudRain, Mic, Goal } from 'lucide-react';
const CommentsSection = lazy(() => import('./components/CommentsSection').then(m => ({
  default: m.CommentsSection
})));
const SocialProofPopup = lazy(() => import('./components/SocialProofPopup').then(m => ({
  default: m.SocialProofPopup
})));
const useDeferredMount = (delay = 3500) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const w = window as any;
    const trigger = () => setMounted(true);
    if ('requestIdleCallback' in window) {
      const id = w.requestIdleCallback(trigger, {
        timeout: delay
      });
      return () => w.cancelIdleCallback && w.cancelIdleCallback(id);
    }
    const id = setTimeout(trigger, delay);
    return () => clearTimeout(id);
  }, [delay]);
  return mounted;
};
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/3QZowYSZuYmiszX4zfxvvf/ChatGPT_Image_17_may_2026,_16_23_22-Photoroom.webp';
const CHECKOUT_URL = 'https://polonia.recursopack.com/cart/40996883398705:1';
const PrimaryCTA = ({
  children,
  subtext,
  scrollTo,
  variant = 'green'
}: {
  children: React.ReactNode;
  subtext?: string;
  scrollTo?: string;
  variant?: 'green' | 'gold';
}) => {
  const handleClick = () => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const cls = variant === 'green' ? 'btn-cta animate-cta-pulse' : 'inline-flex items-center justify-center bg-brand-gold text-brand-navy font-extrabold uppercase tracking-wider rounded-[10px] px-8 py-5 text-lg shadow-[0_6px_20px_rgba(245,166,35,0.4)] hover:shadow-[0_10px_28px_rgba(245,166,35,0.55)] hover:-translate-y-0.5 transition-all duration-200';
  return <div className="flex flex-col items-center w-full max-w-xl mx-auto" data-id="element-0">
      <button onClick={handleClick} className={`${cls} w-full`} data-id="element-1">
        <span className="flex items-center justify-center gap-2 text-base sm:text-lg" data-id="element-2">
          {children}
        </span>
      </button>
      {subtext && <p className="mt-3 text-sm font-medium text-white/80 text-center" data-id="element-3">
          {subtext}
        </p>}
    </div>;
};
const StarsRow = ({
  size = 'w-4 h-4'
}: {
  size?: string;
}) => <div className="flex items-center gap-0.5 text-brand-gold" data-id="element-4">
    {[...Array(5)].map((_, i) => <Star key={i} className={`${size} fill-current`} data-id="element-5" />)}
  </div>;
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const showSocialProof = useDeferredMount(3500);
  const carouselSlides = [{
    title: 'Sześć Artykułów Kodeksu Etyki Trenera',
    subtitle: 'Tom 6 — Administracja',
    image: 'https://cdn.magicpatterns.com/uploads/8DVyxk7Qk6CUyL1Z4XmPbT/pagina_1.webp'
  }, {
    title: 'Szablon Planu Sesji Treningowej',
    subtitle: 'Tom 6 — Administracja',
    image: 'https://cdn.magicpatterns.com/uploads/odF9WXNqsF3B9YmQqvvn52/pagina_2.webp'
  }, {
    title: 'Sesja 15A — Ruchy w Polu Karnym',
    subtitle: 'Tom 4 — Zespół U12-U13',
    image: 'https://cdn.magicpatterns.com/uploads/dnKrk3bnEN6xPvX6Vg8Mcc/pagina_3.webp'
  }, {
    title: 'Sesja 21A — Rzut Wolny: Atak',
    subtitle: 'Tom 3 — Inteligencja U10-U11',
    image: 'https://cdn.magicpatterns.com/uploads/qCnUA99AwTGtsKUUMvUwyS/pagina_4.webp'
  }, {
    title: 'Sesja 23A — Gra 3v3, System Trójkąta',
    subtitle: 'Tom 3 — Inteligencja U10-U11',
    image: 'https://cdn.magicpatterns.com/uploads/aJHdo95pftrzyp6z9QhvFp/pagina_5.webp'
  }, {
    title: 'Sesja 1B — „Moja Piłka, Moja Planeta"',
    subtitle: 'Tom 1 — Podstawy U6-U7',
    image: 'https://cdn.magicpatterns.com/uploads/umWdrjZsjjjtm9wHUFyrjp/pagina_6.webp'
  }];
  const nextSlide = () => setCurrentSlide(p => (p + 1) % carouselSlides.length);
  const prevSlide = () => setCurrentSlide(p => (p - 1 + carouselSlides.length) % carouselSlides.length);
  // Meta Pixel — deferred
  useEffect(() => {
    const loadPixel = () => {
      if ((window as any).fbq) return;
      const f = window as any;
      const n: any = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
      n('init', '1939900849989392');
      n('track', 'PageView');
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(loadPixel, {
        timeout: 3000
      });
    } else {
      setTimeout(loadPixel, 2500);
    }
  }, []);
  const faqs = [{
    q: 'Czy wszystko jest naprawdę w PDF?',
    a: 'Tak. Wszystkie 6 tomów i 6 bonusów to pliki PDF gotowe do pobrania od razu po zakupie. Możesz je drukować lub przeglądać na tablecie, laptopie lub telefonie.'
  }, {
    q: 'Czy to jest odpowiednie dla mojego typu trenowania?',
    a: 'Tak. System obejmuje wszystkie grupy wiekowe od U6 do U13 i wszystkie sytuacje treningowe — od podstaw po zaawansowaną taktykę.'
  }, {
    q: 'Czy potrzebuję specjalnej wiedzy żeby to używać?',
    a: 'Nie. System jest zaprojektowany tak żeby każdy trener mógł go używać od pierwszego dnia. Otwierasz PDF i zaczynasz trenować.'
  }, {
    q: 'Kiedy otrzymam materiały?',
    a: 'Natychmiast po zakupie. Otrzymasz link do pobrania na swój adres email w ciągu kilku minut.'
  }, {
    q: 'Co jeśli nie jestem zadowolony?',
    a: 'Masz 7 dni gwarancji zwrotu pieniędzy bez żadnych pytań. Jeśli system nie spełni Twoich oczekiwań — zwracamy całą kwotę.'
  }, {
    q: 'Dlaczego cena jest tak niska?',
    a: 'Cena premierowa 73 zł jest zablokowana tylko dla pierwszych 200 trenerów. Potem wraca do regularnej.'
  }];
  const tomos = [{
    icon: BookOpen,
    num: '1',
    color: '#1B4F8A',
    title: 'Podstawy — U6 i U7',
    desc: 'Makroplan 40 tygodni, ćwiczenia adaptacyjne i koordynacyjne, gry z piłką, karty oceny zawodnika i listy obecności. Gotowe do użycia od pierwszego dnia.'
  }, {
    icon: Goal,
    num: '2',
    color: '#C0392B',
    title: 'Technika — U8 i U9',
    desc: 'Prowadzenie i kontrola piłki, podanie i przyjęcie, zwody i dryblingi, strzał na bramkę, gra 1v1 i 2v2 — wszystko z kartami oceny technicznej.'
  }, {
    icon: Brain,
    num: '3',
    color: '#6C3483',
    title: 'Inteligencja — U10 i U11',
    desc: 'Podejmowanie decyzji pod presją, przejścia atak-obrona, gra pozycyjna, schematy taktyczne i karty oceny inteligencji boiskowej.'
  }, {
    icon: Users,
    num: '4',
    color: '#1E8449',
    title: 'Zespół — U12 i U13',
    desc: 'Systemy gry, taktyka obronna i ofensywna, stałe fragmenty gry, pressing, kontratak, plany meczowe i karty oceny zespołowej.'
  }, {
    icon: Compass,
    num: '5',
    color: '#117A65',
    title: 'Atlas Trenera — wszystkie poziomy',
    desc: 'Biblioteka 200+ ćwiczeń, schematy boisk, szablony planów treningowych, diagramy taktyczne, słownik pojęć i indeks ćwiczeń.'
  }, {
    icon: FileText,
    num: '6',
    color: '#784212',
    title: 'Administracja — zarządzanie',
    desc: 'Szablony komunikacji z rodzicami, karty oceny indywidualnej, listy obecności, raport miesięczny, psychologia pracy z młodym zawodnikiem.'
  }];
  const bonuses = [{
    icon: Rocket,
    title: 'Bonus 1 — Szybki Start Trenera',
    desc: 'Kompletna lista kontrolna przed każdym treningiem. Żeby każda sesja przebiegała bez zakłóceń.'
  }, {
    icon: LifeBuoy,
    title: 'Bonus 2 — 30 Gier Ratunkowych',
    desc: '30 gotowych gier ratunkowych na każdą nieoczekiwaną sytuację. Nigdy więcej chaosu.'
  }, {
    icon: CheckSquare,
    title: 'Bonus 3 — Checklista Mistrza',
    desc: 'Profesjonalny system kontroli jakości treningu od pierwszego do ostatniego gwizdka.'
  }, {
    icon: Megaphone,
    title: 'Bonus 4 — 50 Komend Trenera',
    desc: '50 gotowych komend które natychmiast organizują grupę i budują Twój autorytet.'
  }, {
    icon: CloudRain,
    title: 'Bonus 5 — Plan B na Deszcz i Chaos',
    desc: 'Jasne rozwiązania na dzień gdy pogoda lub miejsce nie sprzyjają. Masz plan.'
  }, {
    icon: Mic,
    title: 'Bonus 6 — Szybkie Odprawy Trenera',
    desc: 'Gotowe przemówienia które motywują i organizują drużynę w 3 minuty.'
  }];
  const testimonialsTop = [{
    quote: 'W końcu coś co działa w praktyce. Już w pierwszym tygodniu znalazłem 3 błędy w swojej metodyce których wcześniej nie widziałem.',
    name: 'Marek W.',
    role: 'Trener piłki nożnej, Warszawa'
  }, {
    quote: 'Skróciłem czas przygotowania o 80% w pierwszym miesiącu. Nie wierzyłem że PDF może tyle zmienić.',
    name: 'Tomasz K.',
    role: 'Trener piłki nożnej, Kraków'
  }, {
    quote: 'W końcu uwolniłem się od wymyślania planu na nowo każdego tygodnia. Ten system naprawdę działa.',
    name: 'Rafał B.',
    role: 'Trener piłki nożnej, Poznań'
  }];
  const differentiators = [{
    icon: Target,
    title: 'DZIAŁA W PRAKTYCE',
    desc: 'Każda strona to narzędzie gotowe do użycia na Twoim treningu dzisiaj. Nie podręcznik akademicki.'
  }, {
    icon: Rocket,
    title: 'ZACZNIJ OD RAZU',
    desc: 'Bez uczenia się oprogramowania. Bez szkoleń. Otwórz PDF i zacznij trenować.'
  }, {
    icon: Layers,
    title: 'KOMPLETNY',
    desc: 'Plany treningowe, taktyka, ocena zawodnika, zarządzanie grupą i komunikacja z rodzicami.'
  }, {
    icon: InfinityIcon,
    title: 'TWOJE NA ZAWSZE',
    desc: 'Jednorazowa płatność. Brak opłat miesięcznych. Używaj kiedy chcesz na każdym urządzeniu.'
  }];
  return <div className="bg-white text-brand-text-dark overflow-x-hidden" data-id="element-6">
      {/* TOP ANNOUNCEMENT BAR */}
      <div className="w-full bg-brand-danger py-3 px-4" data-id="element-7">
        <div className="max-w-5xl mx-auto flex items-center justify-center gap-3 text-center" data-id="element-8">
          <div className="w-2 h-2 rounded-full bg-white animate-pulse-fast shrink-0 shadow-[0_0_8px_rgba(255,255,255,0.9)]" data-id="element-9" />
          <p className="text-xs md:text-sm font-bold tracking-wider text-white uppercase" data-id="element-10">
            OFERTA STARTOWA — TYLKO DLA PIERWSZYCH 200 TRENERÓW · CENA 73 ZŁ
          </p>
        </div>
      </div>

      {/* HERO — navy */}
      <section className="section-dark relative overflow-hidden" data-id="element-11">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F1F3D] via-[#0F1F3D] to-[#1A2744] pointer-events-none" data-id="element-12" />
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-brand-green/10 rounded-full blur-[120px] pointer-events-none" data-id="element-13" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 md:pt-16 pb-12" data-id="element-14">
          <div className="text-center space-y-5" data-id="element-15">
            <span className="inline-block text-[11px] md:text-xs font-bold tracking-widest uppercase bg-brand-gold/15 border border-brand-gold/40 text-brand-gold rounded-full px-4 py-1.5" data-id="element-16">
              ⚠️ Uwaga — Trenerze piłki nożnej
            </span>
            <h1 className="text-[32px] sm:text-5xl md:text-6xl font-black text-white leading-[1.05] tracking-tight max-w-4xl mx-auto" data-id="element-17">
              Zaplanuj cały sezon{' '}
              <span className="text-brand-green" data-id="element-18">w kilka minut</span> — bez
              wymyślania od zera co tydzień
            </h1>
            <p className="text-base md:text-xl text-slate-300 leading-relaxed font-medium max-w-3xl mx-auto" data-id="element-19">
              Pierwszy w Polsce kompletny system treningowy{' '}
              <span className="text-white font-bold" data-id="element-20">"Otwórz i Trenuj"</span> —
              z gotowymi ćwiczeniami, schematami i planami dla każdego poziomu
              od U6 do U13.
            </p>
            <p className="text-lg md:text-2xl font-extrabold text-brand-gold" data-id="element-21">
              Futbol Gotowy: System 365
            </p>
          </div>

          {/* Hero bundle image */}
          <div className="relative w-full max-w-2xl md:max-w-3xl mx-auto mt-8 mb-8" style={{
          aspectRatio: '1280 / 853'
        }} data-id="element-22">
            <img src={HERO_IMAGE} alt="Futbol Gotowy: System 365 — Kompletny Zestaw" width={1280} height={853} fetchPriority="high" decoding="async" loading="eager" className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl" data-id="element-23" />
          </div>

          {/* Social proof + CTA */}
          <div className="flex flex-col items-center gap-4 mb-2" data-id="element-24">
            <div className="flex items-center gap-2" data-id="element-25">
              <StarsRow size="w-5 h-5" data-id="element-26" />
              <span className="text-white font-bold" data-id="element-27">4.9/5</span>
              <span className="text-slate-300 text-sm" data-id="element-28">
                (ponad 1 200 opinii)
              </span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold text-sm" data-id="element-29">
              <Flame className="w-4 h-4 shrink-0" data-id="element-30" />
              67 trenerów wybrało ten system dzisiaj
            </div>
          </div>

          <div className="mt-6" data-id="element-31">
            <PrimaryCTA subtext="⚡ Natychmiastowy dostęp • 🛡️ Gwarancja 7 dni • 💳 Jednorazowa płatność" scrollTo="oferta" data-id="element-32">
              ⚽ TAK! CHCĘ TEN KOMPLETNY SYSTEM
            </PrimaryCTA>
          </div>

          <p className="text-center text-slate-400 text-sm mt-8" data-id="element-33">
            🇵🇱 Zasób numer 1 wybierany przez polskich trenerów piłki nożnej
          </p>
        </div>
      </section>

      {/* PAIN / NARRATIVE — white */}
      <section className="section-light py-16 md:py-20 px-4 sm:px-6" data-id="element-34">
        <div className="max-w-3xl mx-auto" data-id="element-35">
          <div className="border-l-4 border-brand-gold pl-6" data-id="element-36">
            <p className="text-lg md:text-xl leading-relaxed text-brand-text-dark font-medium" data-id="element-37">
              <span className="text-brand-navy font-extrabold" data-id="element-38">
                Bycie trenerem piłki nożnej w Polsce dzisiaj nie jest proste.
              </span>{' '}
              Co tydzień musisz tworzyć nowy plan treningowy od zera. Każda
              grupa wiekowa wymaga innego podejścia. Rodzice pytają o konkretne
              wyniki. Pracujesz ciężko każdego dnia — ale nadal czujesz że
              brakuje Ci jasnego systemu.{' '}
              <span className="bg-brand-gold/20 text-brand-navy font-bold px-1 underline decoration-brand-gold decoration-2 underline-offset-2" data-id="element-39">
                To nie jest Twoja wina.
              </span>{' '}
              Nikt nigdy nie dał Ci systemu który naprawdę działa. Aż do dziś.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS HERO — light alt */}
      <section className="section-alt py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-40">
        <div className="max-w-5xl mx-auto" data-id="element-41">
          <h2 className="text-2xl md:text-4xl font-extrabold text-center text-brand-navy mb-3" data-id="element-42">
            Ponad 1 200 trenerów nam zaufało
          </h2>
          <p className="text-center text-brand-text-soft mb-10" data-id="element-43">
            Oto co mówią o systemie
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-44">
            {testimonialsTop.map((t, i) => <div key={i} className="card-soft p-6 relative border-l-4 border-brand-green" data-id="element-45">
                <Quote className="w-6 h-6 text-brand-green/30 mb-3" data-id="element-46" />
                <p className="text-brand-text-dark leading-relaxed italic mb-4" data-id="element-47">
                  "{t.quote}"
                </p>
                <StarsRow data-id="element-48" />
                <p className="text-brand-navy font-bold text-sm mt-2" data-id="element-49">
                  — {t.name}
                </p>
                <p className="text-brand-text-soft text-xs" data-id="element-50">{t.role}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER — navy */}
      <section className="section-dark py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-51">
        <div className="max-w-5xl mx-auto" data-id="element-52">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-white mb-10" data-id="element-53">
            Zmiana którą poczujesz od pierwszego treningu
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-54">
            {/* PRZED */}
            <div className="bg-[#1A1A2E] rounded-2xl p-6 md:p-8" data-id="element-55">
              <h3 className="text-xl font-bold text-white mb-1" data-id="element-56">
                PRZED SYSTEMEM
              </h3>
              <p className="text-slate-400 text-sm mb-6" data-id="element-57">
                Co Cię to kosztuje dzisiaj
              </p>
              <ul className="space-y-4" data-id="element-58">
                {['Piszesz nowy plan treningowy co tydzień od zera.', 'Nie wiesz co robić gdy pada deszcz lub masz małą grupę.', 'Rodzice pytają o wyniki ale nie masz nic do pokazania.', 'Zawodnicy nie rozwijają się systematycznie.', 'Improwizujesz na każdym treningu.', 'Brakuje Ci jasnego systemu pracy.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-59">
                    <div className="mt-0.5 bg-brand-danger/15 p-1 rounded-full shrink-0" data-id="element-60">
                      <X className="w-4 h-4 text-brand-danger" data-id="element-61" />
                    </div>
                    <span className="text-slate-300 leading-relaxed" data-id="element-62">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* PO */}
            <div className="bg-brand-green rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-63">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl pointer-events-none" data-id="element-64" />
              <h3 className="text-xl font-bold text-white mb-1 relative" data-id="element-65">
                PO SYSTEMIE
              </h3>
              <p className="text-white/80 text-sm mb-6 relative" data-id="element-66">
                Twoja praca jutro
              </p>
              <ul className="space-y-4 relative" data-id="element-67">
                {['Masz gotowy plan na pełne 40 tygodni — do użycia od zaraz.', 'Masz gotowe treści na każdy trening niezależnie od warunków.', 'Masz gotowe raporty dla rodziców na każdy miesiąc.', 'System oceny indywidualnej na każdym poziomie.', 'Otwierasz PDF i trenujesz — zero stresu.', 'Każda sesja zorganizowana od pierwszej do ostatniej minuty.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-68">
                    <div className="mt-0.5 bg-white/20 p-1 rounded-full shrink-0" data-id="element-69">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-70" />
                    </div>
                    <span className="text-white font-medium leading-relaxed" data-id="element-71">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PARA QUIÉN ES — light alt */}
      <section className="section-alt py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-72">
        <div className="max-w-3xl mx-auto" data-id="element-73">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-brand-navy mb-10" data-id="element-74">
            Ten system jest dla Ciebie jeśli...
          </h2>
          <ul className="space-y-4 card-soft p-6 md:p-8" data-id="element-75">
            {['Jesteś trenerem piłki nożnej, szkoleniowcem lub instruktorem w klubie, szkole lub akademii.', 'Masz dość ciężkiej pracy bez jasnego systemu który ją wspiera.', 'Chcesz przestać wymyślać plan treningowy co tydzień samodzielnie.', 'Chcesz żeby trening działał dobrze nawet w dniu gdy nie jesteś w 100% przygotowany.', 'Chcesz żeby rodzice widzieli konkretne wyniki pracy z ich dziećmi.', 'Chcesz rozwijać każdego zawodnika systematycznie i mierzalnie.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-76">
                <div className="mt-0.5 bg-brand-green/10 p-1 rounded-full shrink-0" data-id="element-77">
                  <Check className="w-5 h-5 text-brand-green" strokeWidth={3} data-id="element-78" />
                </div>
                <span className="text-brand-text-dark text-base md:text-lg leading-relaxed" data-id="element-79">
                  {item}
                </span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* WHY DIFFERENT — white */}
      <section className="section-light py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-80">
        <div className="max-w-5xl mx-auto" data-id="element-81">
          <div className="text-center mb-10" data-id="element-82">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-3" data-id="element-83">
              Dlaczego ten system jest inny
            </h2>
            <p className="text-brand-text-soft text-lg max-w-2xl mx-auto" data-id="element-84">
              Żadnej teorii. Żadnych miesięcznych subskrypcji. Żadnych drogich
              konsultantów. Tylko gotowe narzędzia.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-85">
            {differentiators.map((d, i) => <div key={i} className="card-soft p-6 border-t-4 border-brand-green" data-id="element-86">
                <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center mb-4" data-id="element-87">
                  <d.icon className="w-6 h-6 text-brand-green" data-id="element-88" />
                </div>
                <h3 className="font-extrabold text-brand-navy mb-2 tracking-wide text-sm" data-id="element-89">
                  {d.title}
                </h3>
                <p className="text-brand-text-soft text-sm leading-relaxed" data-id="element-90">
                  {d.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6 TOMOS — light alt with colored borders */}
      <section className="section-alt py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-91">
        <div className="max-w-6xl mx-auto" data-id="element-92">
          <div className="text-center mb-10" data-id="element-93">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-3" data-id="element-94">
              6 Tomów Kompletnego Systemu
            </h2>
            <p className="text-brand-text-soft text-lg" data-id="element-95">
              Cyfrowe narzędzia w formacie PDF — gotowe do druku lub
              wyświetlenia na tablecie.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-96">
            {tomos.map((t, i) => <div key={i} className="card-soft p-6 border-t-4 transition-transform hover:-translate-y-1" style={{
            borderTopColor: t.color
          }} data-id="element-97">
                <div className="flex items-center gap-3 mb-4" data-id="element-98">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{
                backgroundColor: `${t.color}15`
              }} data-id="element-99">
                    <t.icon className="w-6 h-6" style={{
                  color: t.color
                }} data-id="element-100" />
                  </div>
                  <span className="text-[11px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{
                color: t.color,
                backgroundColor: `${t.color}15`
              }} data-id="element-101">
                    Tom {t.num}
                  </span>
                </div>
                <h3 className="text-lg font-extrabold text-brand-navy mb-2" data-id="element-102">
                  {t.title}
                </h3>
                <p className="text-brand-text-soft text-sm leading-relaxed mb-4" data-id="element-103">
                  {t.desc}
                </p>
                <p className="text-sm font-bold" data-id="element-104">
                  <span className="text-brand-danger line-through mr-2" data-id="element-105">
                    150 zł
                  </span>
                  <span className="text-brand-green" data-id="element-106">Wliczone</span>
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CAROUSEL — white */}
      <section className="section-light py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-107">
        <div className="max-w-3xl mx-auto" data-id="element-108">
          <div className="text-center mb-8" data-id="element-109">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-3" data-id="element-110">
              Zobacz materiał w środku
            </h2>
            <p className="text-brand-text-soft text-lg" data-id="element-111">
              Podgląd tego co znajdziesz w 6 tomach systemu.
            </p>
            <p className="text-brand-green font-bold mt-3" data-id="element-112">
              {currentSlide + 1} / {carouselSlides.length}
            </p>
          </div>

          <div className="relative max-w-2xl mx-auto" data-id="element-113">
            <button onClick={prevSlide} aria-label="Poprzednia" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 md:-translate-x-12 w-11 h-11 rounded-full bg-white border border-slate-200 flex items-center justify-center text-brand-navy hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors z-20 shadow-card" data-id="element-114">
              <ChevronLeft className="w-5 h-5" data-id="element-115" />
            </button>
            <button onClick={nextSlide} aria-label="Następna" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 md:translate-x-12 w-11 h-11 rounded-full bg-white border border-slate-200 flex items-center justify-center text-brand-navy hover:bg-brand-green hover:text-white hover:border-brand-green transition-colors z-20 shadow-card" data-id="element-116">
              <ChevronRight className="w-5 h-5" data-id="element-117" />
            </button>

            <div className="rounded-2xl shadow-card relative bg-white overflow-hidden border border-slate-200" style={{
            aspectRatio: '3 / 4'
          }} data-id="element-118">
              {carouselSlides.map((slide, i) => <div key={i} className={`absolute inset-0 bg-white ${i === currentSlide ? 'block' : 'hidden'}`} data-id="element-119">
                  <img src={slide.image} alt={slide.title} width={800} height={1066} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" className="block w-full h-full object-contain" data-id="element-120" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-navy/95 via-brand-navy/70 to-transparent p-5 pt-16 pointer-events-none" data-id="element-121">
                    <p className="text-[10px] uppercase tracking-widest text-brand-gold font-bold mb-1" data-id="element-122">
                      {slide.subtitle}
                    </p>
                    <h3 className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-123">
                      {slide.title}
                    </h3>
                  </div>
                </div>)}
            </div>

            <div className="flex justify-center gap-2 mt-6" data-id="element-124">
              {carouselSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} aria-label={`Idź do strony ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-brand-green w-8' : 'bg-slate-300 hover:bg-slate-400 w-2'}`} data-id="element-125" />)}
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES — navy */}
      <section className="section-dark py-16 md:py-20 px-4 sm:px-6 cv-auto" data-id="element-126">
        <div className="max-w-5xl mx-auto" data-id="element-127">
          <div className="text-center mb-10" data-id="element-128">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3" data-id="element-129">
              6 Specjalnych Bonusów w Zestawie
            </h2>
            <p className="text-brand-gold font-bold uppercase tracking-wider text-sm" data-id="element-130">
              (Bez dodatkowych kosztów)
            </p>
            <div className="inline-block mt-4 px-5 py-2 rounded-full bg-white/5 border border-white/10" data-id="element-131">
              <p className="text-white" data-id="element-132">
                Łączna wartość bonusów:{' '}
                <span className="font-extrabold" data-id="element-133">600 zł</span>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-134">
            {bonuses.map((b, i) => <div key={i} className="bg-[#1A2744] border-l-4 border-brand-gold rounded-r-xl p-5 flex items-start gap-4" data-id="element-135">
                <div className="w-11 h-11 rounded-lg bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center shrink-0" data-id="element-136">
                  <b.icon className="w-5 h-5 text-brand-gold" data-id="element-137" />
                </div>
                <div className="flex-1" data-id="element-138">
                  <div className="flex items-start justify-between gap-2 mb-1" data-id="element-139">
                    <h4 className="text-white font-extrabold text-base leading-tight" data-id="element-140">
                      {b.title}
                    </h4>
                    <span className="px-2 py-0.5 text-[10px] font-extrabold bg-brand-green text-white rounded uppercase tracking-wider shrink-0" data-id="element-141">
                      Gratis
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed" data-id="element-142">
                    {b.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PRICING — navy gradient */}
      <section id="oferta" className="py-16 md:py-20 px-4 sm:px-6 cv-auto scroll-mt-8" style={{
      background: 'linear-gradient(160deg, #0F1F3D 0%, #1A2744 100%)'
    }} data-id="element-143">
        <div className="max-w-2xl mx-auto" data-id="element-144">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white text-center mb-8" data-id="element-145">
            Kompletna Oferta "Futbol Gotowy: System 365"
          </h2>

          <div className="bg-white rounded-3xl p-6 md:p-10 relative overflow-hidden shadow-card text-left" data-id="element-146">
            <div className="absolute top-0 right-0 bg-brand-danger text-white text-xs font-extrabold px-12 py-2 rotate-45 translate-x-10 translate-y-6 shadow-lg z-20 tracking-wider" data-id="element-147">
              95% OFF
            </div>

            <div className="flex justify-center mb-6" data-id="element-148">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-gold/15 border border-brand-gold/40 text-[#92400E] text-xs font-extrabold tracking-wider uppercase" data-id="element-149">
                <Trophy className="w-4 h-4" data-id="element-150" />
                NAJPOPULARNIEJSZY WYBÓR
              </div>
            </div>

            <p className="text-brand-text-soft font-bold uppercase tracking-widest text-xs mb-4 border-b border-slate-200 pb-3" data-id="element-151">
              ŁĄCZNA WARTOŚĆ MATERIAŁÓW:
            </p>

            <ul className="space-y-3 mb-6" data-id="element-152">
              {[{
              name: 'Tom 1 — Podstawy U6-U7',
              price: '150 zł'
            }, {
              name: 'Tom 2 — Technika U8-U9',
              price: '150 zł'
            }, {
              name: 'Tom 3 — Inteligencja U10-U11',
              price: '150 zł'
            }, {
              name: 'Tom 4 — Zespół U12-U13',
              price: '150 zł'
            }, {
              name: 'Tom 5 — Atlas Trenera',
              price: '150 zł'
            }, {
              name: 'Tom 6 — Administracja',
              price: '150 zł'
            }, {
              name: '6 Bonusów specjalnych',
              price: '600 zł'
            }].map((item, i) => <li key={i} className="flex justify-between items-center text-brand-text-dark text-sm" data-id="element-153">
                  <span className="flex items-center gap-2" data-id="element-154">
                    <Check className="w-4 h-4 text-brand-green" strokeWidth={3} data-id="element-155" />
                    {item.name}
                  </span>
                  <span className="font-bold" data-id="element-156">{item.price}</span>
                </li>)}
            </ul>

            <div className="border-t border-slate-200 pt-4 mb-6 flex justify-between items-center" data-id="element-157">
              <span className="text-brand-text-soft font-medium text-sm" data-id="element-158">
                WARTOŚĆ RZECZYWISTA:
              </span>
              <span className="text-lg text-brand-text-soft line-through decoration-brand-danger decoration-2 font-bold" data-id="element-159">
                1 500 zł
              </span>
            </div>

            <div className="text-center bg-brand-light rounded-2xl p-5 mb-6" data-id="element-160">
              <p className="text-brand-green font-extrabold tracking-widest uppercase text-xs mb-1" data-id="element-161">
                TYLKO DZISIAJ
              </p>
              <div className="flex items-baseline justify-center gap-2" data-id="element-162">
                <span className="font-black tracking-tighter leading-none text-brand-gold" style={{
                fontSize: 'clamp(64px, 14vw, 88px)'
              }} data-id="element-163">
                  73
                </span>
                <span className="text-3xl font-extrabold text-brand-gold" data-id="element-164">
                  zł
                </span>
              </div>
              <p className="text-brand-text-soft text-sm mt-2" data-id="element-165">
                💳 Jednorazowa płatność • ⚡ Natychmiastowy dostęp
              </p>
              <p className="text-brand-danger flex items-center justify-center gap-1.5 font-bold text-sm mt-1" data-id="element-166">
                <Clock className="w-4 h-4" data-id="element-167" /> Tylko dla pierwszych 200 trenerów
              </p>
            </div>

            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="btn-cta animate-cta-pulse w-full no-underline" data-id="element-168">
              <span className="flex items-center justify-center gap-2 text-base sm:text-lg" data-id="element-169">
                ⚽ KUP TERAZ — 73 ZŁ
              </span>
            </a>
            <p className="mt-3 text-xs text-center text-brand-text-soft font-medium" data-id="element-170">
              ⚡ Natychmiastowy dostęp • 💳 Jednorazowa płatność • 🛡️ Gwarancja
              7 dni
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2 text-[11px] text-brand-text-soft font-medium" data-id="element-171">
              <div className="flex items-center gap-1.5 justify-center bg-brand-light rounded-lg py-2 px-3" data-id="element-172">
                🔒 Bezpieczny zakup
              </div>
              <div className="flex items-center gap-1.5 justify-center bg-brand-light rounded-lg py-2 px-3" data-id="element-173">
                📥 Natychmiastowe pobieranie
              </div>
              <div className="flex items-center gap-1.5 justify-center bg-brand-light rounded-lg py-2 px-3" data-id="element-174">
                💳 Jednorazowa płatność 73 zł
              </div>
              <div className="flex items-center gap-1.5 justify-center bg-brand-light rounded-lg py-2 px-3" data-id="element-175">
                🛡️ Gwarancja 7 dni
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GUARANTEE — bold navy with stadium glow */}
      <section className="relative py-16 md:py-20 px-4 sm:px-6 cv-auto overflow-hidden bg-brand-navy" data-id="element-176">
        {/* Decorative glows */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-brand-green/20 rounded-full blur-[120px] pointer-events-none" data-id="element-177" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-brand-gold/10 rounded-full blur-[120px] pointer-events-none" data-id="element-178" />
        {/* Subtle field-line top accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-green to-transparent" data-id="element-179" />

        <div className="relative max-w-3xl mx-auto" data-id="element-180">
          <div className="text-center mb-8" data-id="element-181">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-green/15 border border-brand-green/40 text-brand-green text-xs font-extrabold tracking-widest uppercase" data-id="element-182">
              <ShieldCheck className="w-4 h-4" data-id="element-183" />
              Twoja inwestycja bez ryzyka
            </span>
          </div>

          <div className="relative bg-gradient-to-br from-[#1A2744] to-[#0F1F3D] rounded-3xl p-8 md:p-12 border-2 border-brand-green/40 shadow-[0_20px_60px_rgba(26,124,62,0.25)]" data-id="element-184">
            {/* Corner ribbon */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-green text-white text-xs font-extrabold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-cta" data-id="element-185">
              7 Dni Gwarancji
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 text-center md:text-left" data-id="element-186">
              <div className="relative shrink-0" data-id="element-187">
                <div className="absolute inset-0 bg-brand-green/30 rounded-full blur-2xl" data-id="element-188" />
                <div className="relative w-24 h-24 rounded-full bg-brand-green flex items-center justify-center border-4 border-white/10 shadow-cta" data-id="element-189">
                  <ShieldCheck className="w-12 h-12 text-white" strokeWidth={2.2} data-id="element-190" />
                </div>
              </div>
              <div className="flex-1" data-id="element-191">
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight" data-id="element-192">
                  100% Gwarancja Zwrotu Pieniędzy
                </h3>
                <p className="text-slate-300 leading-relaxed text-base md:text-lg" data-id="element-193">
                  Pobierz wszystko. Przeczytaj. Zastosuj na treningu. Jeśli nie
                  jesteś w 100% zadowolony w ciągu 7 dni —{' '}
                  <span className="font-extrabold text-brand-gold" data-id="element-194">
                    zwracamy całą kwotę
                  </span>
                  .{' '}
                  <span className="font-bold text-white" data-id="element-195">
                    Bez pytań. Bez procedur.
                  </span>{' '}
                  Całe ryzyko bierzemy na siebie.
                </p>
              </div>
            </div>

            {/* Trust strip */}
            <div className="grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-white/10" data-id="element-196">
              {[{
              icon: '🔒',
              label: 'Bezpieczny zakup'
            }, {
              icon: '⚡',
              label: 'Natychmiastowy dostęp'
            }, {
              icon: '🛡️',
              label: '7 dni gwarancji'
            }].map((t, i) => <div key={i} className="text-center" data-id="element-197">
                  <div className="text-2xl mb-1" data-id="element-198">{t.icon}</div>
                  <div className="text-[11px] md:text-xs text-slate-300 font-semibold" data-id="element-199">
                    {t.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* COMMENTS — off-white with decorative header */}
      <section className="relative section-light py-16 md:py-20 px-4 sm:px-6 cv-auto overflow-hidden" data-id="element-200">
        {/* Top decorative band */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-brand-green via-brand-gold to-brand-green opacity-80" data-id="element-201" />
        <div className="absolute top-10 -left-20 w-72 h-72 bg-brand-green/5 rounded-full blur-3xl pointer-events-none" data-id="element-202" />
        <div className="absolute bottom-10 -right-20 w-72 h-72 bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" data-id="element-203" />

        <div className="relative" data-id="element-204">
          <Suspense fallback={<div className="h-[600px]" data-id="element-206" />} data-id="element-205">
            <CommentsSection data-id="element-207" />
          </Suspense>
        </div>
      </section>

      {/* STATS BANNER — punchy break */}
      <section className="relative py-12 md:py-16 px-4 sm:px-6 overflow-hidden bg-brand-navy cv-auto" data-id="element-208">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(26,124,62,0.4) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} data-id="element-209" />
        <div className="relative max-w-5xl mx-auto" data-id="element-210">
          <h2 className="text-center text-white text-2xl md:text-3xl font-extrabold mb-10" data-id="element-211">
            Dlaczego trenerzy wybierają{' '}
            <span className="text-brand-green" data-id="element-212">Futbol Gotowy</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-213">
            {[{
            num: '1 200+',
            label: 'Zadowolonych trenerów',
            accent: 'text-brand-green'
          }, {
            num: '4.9',
            label: 'Średnia ocena ★★★★★',
            accent: 'text-brand-gold'
          }, {
            num: '200+',
            label: 'Ćwiczeń w bibliotece',
            accent: 'text-brand-green'
          }, {
            num: '40',
            label: 'Tygodni planowania',
            accent: 'text-brand-gold'
          }].map((s, i) => <div key={i} className="text-center" data-id="element-214">
                <div className={`text-4xl md:text-5xl font-black ${s.accent} leading-none`} data-id="element-215">
                  {s.num}
                </div>
                <div className="text-slate-300 text-xs md:text-sm font-medium mt-2 leading-tight" data-id="element-216">
                  {s.label}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ — light alt with field-line accent */}
      <section className="relative section-alt py-16 md:py-20 px-4 sm:px-6 cv-auto overflow-hidden" data-id="element-217">
        {/* Decorative side stripe */}
        <div className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-brand-green via-brand-gold to-brand-green opacity-60 hidden md:block" data-id="element-218" />
        <div className="absolute top-20 right-10 w-64 h-64 bg-brand-green/5 rounded-full blur-3xl pointer-events-none" data-id="element-219" />

        <div className="relative max-w-3xl mx-auto" data-id="element-220">
          <div className="text-center mb-10" data-id="element-221">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-brand-green/30 text-brand-green text-xs font-extrabold tracking-widest uppercase shadow-sm" data-id="element-222">
              ❓ Masz pytania?
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mt-4 mb-2" data-id="element-223">
              Najczęściej Zadawane Pytania
            </h2>
            <p className="text-brand-text-soft" data-id="element-224">
              Wszystko co musisz wiedzieć przed zakupem
            </p>
          </div>

          <div className="space-y-3" data-id="element-225">
            {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return <div key={index} className={`bg-white rounded-xl border overflow-hidden transition-all duration-200 ${isOpen ? 'border-brand-green shadow-card' : 'border-slate-200 hover:border-brand-green/40 hover:shadow-sm'}`} data-id="element-226">
                  <button onClick={() => setOpenFaq(isOpen ? null : index)} className="w-full px-5 py-4 flex items-center justify-between text-left transition-colors" data-id="element-227">
                    <span className="flex items-center gap-3 pr-4" data-id="element-228">
                      <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-extrabold shrink-0 transition-colors ${isOpen ? 'bg-brand-green text-white' : 'bg-brand-green/10 text-brand-green'}`} data-id="element-229">
                        {index + 1}
                      </span>
                      <span className="text-base md:text-lg font-bold text-brand-navy" data-id="element-230">
                        {faq.q}
                      </span>
                    </span>
                    <ChevronDown className={`w-5 h-5 text-brand-green shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-231" />
                  </button>
                  <div className={`px-5 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 pb-4 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-232">
                    <p className="text-brand-text-dark leading-relaxed border-t border-slate-200 pt-3 text-sm md:text-base pl-10" data-id="element-233">
                      {faq.a}
                    </p>
                  </div>
                </div>;
          })}
          </div>

          {/* Reassurance card after FAQ */}
          <div className="mt-10 bg-white rounded-2xl p-6 border border-slate-200 shadow-card flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left" data-id="element-234">
            <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center shrink-0" data-id="element-235">
              <span className="text-2xl" data-id="element-236">💬</span>
            </div>
            <p className="text-brand-text-dark text-sm flex-1" data-id="element-237">
              <span className="font-bold text-brand-navy" data-id="element-238">
                Masz jeszcze wątpliwości?
              </span>{' '}
              Pamiętaj że masz 7 dni gwarancji zwrotu — możesz wszystko
              sprawdzić bez ryzyka.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA — green */}
      <section className="py-16 md:py-20 px-4 sm:px-6" style={{
      background: 'linear-gradient(135deg, #1A7C3E 0%, #0F1F3D 100%)'
    }} data-id="element-239">
        <div className="max-w-3xl mx-auto text-center" data-id="element-240">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3" data-id="element-241">
            Jesteś gotowy żeby zmienić swój sposób trenowania?
          </h2>
          <p className="text-white/85 mb-8 text-base md:text-lg" data-id="element-242">
            Dołącz do ponad 1 200 trenerów którzy wybrali ten system.
          </p>
          <PrimaryCTA variant="gold" subtext="⚡ Natychmiastowy dostęp • 🛡️ Gwarancja 7 dni • 💳 Jednorazowa płatność" scrollTo="oferta" data-id="element-243">
            ⚽ TAK! CHCĘ ODZYSKAĆ MÓJ CZAS
          </PrimaryCTA>
        </div>
      </section>

      {/* FOOTER — navy */}
      <footer className="section-dark py-12 px-4 sm:px-6" data-id="element-244">
        <div className="max-w-3xl mx-auto text-center space-y-5" data-id="element-245">
          <div className="flex items-center justify-center gap-2 text-white font-extrabold text-lg" data-id="element-246">
            <ClipboardList className="w-5 h-5 text-brand-green" data-id="element-247" />
            Futbol Gotowy — System 365
          </div>
          <p className="text-slate-300 text-sm leading-relaxed max-w-xl mx-auto" data-id="element-248">
            Najlepszy sposób na trenowanie piłki nożnej bez przygotowywania
            wszystkiego od zera. Praktyczne narzędzia dla trenerów którzy chcą
            konkretnych wyników.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-400" data-id="element-249">
            <a href="#" className="hover:text-white transition-colors" data-id="element-250">
              🔒 Polityka Prywatności
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-251">
              Regulamin
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-252">
              Polityka Cookies
            </a>
          </div>
          <p className="text-slate-500 text-xs pt-3" data-id="element-253">
            © Futbol Gotowy 2026 — Wszelkie prawa zastrzeżone. Produkt cyfrowy
            — PDF do natychmiastowego pobrania.
          </p>
          <p className="text-slate-600 text-[11px] max-w-2xl mx-auto leading-relaxed" data-id="element-254">
            Opisane wyniki są przykładami rzeczywistymi ale nie gwarantowanymi.
            Indywidualne rezultaty mogą się różnić w zależności od zastosowania.
          </p>
        </div>
      </footer>

      {showSocialProof && <Suspense fallback={null} data-id="element-255">
          <SocialProofPopup data-id="element-256" />
        </Suspense>}
    </div>;
}